import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useUser } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import type { Player } from '@/types/player';
import type { UserRoster } from '@/types/rosterFormats';
import { ROSTER_FORMATS } from '@/types/rosterFormats';

interface MultiRosterContextType {
  rosters: UserRoster[];
  activeRosterId: string | null;
  activeRoster: UserRoster | null;
  activeRosterPlayers: Player[];
  playersPool: Player[];
  createRoster: (name: string, formatId: string) => Promise<string>;
  deleteRoster: (rosterId: string) => Promise<void>;
  setActiveRoster: (rosterId: string) => Promise<void>;
  updateRosterName: (rosterId: string, name: string) => Promise<void>;
  updateStarterLimits: (rosterId: string, limits: Partial<UserRoster['customStarterLimits']>) => Promise<void>;
  addPlayerToRoster: (rosterId: string, player: Player) => Promise<void>;
  removePlayerFromRoster: (rosterId: string, playerId: string) => Promise<void>;
  replaceRosterPlayers: (rosterId: string, players: Player[]) => Promise<void>;
  replaceRosterPlayersWithStarters: (rosterId: string, players: Player[], starterPlayerIds?: string[]) => Promise<void>;
  togglePlayerStarter: (rosterId: string, playerId: string, isStarter: boolean) => Promise<void>;
  isPlayerInActiveRoster: (playerId: string) => boolean;
  getStarterLimits: (rosterId: string) => {
    QB: number;
    RB: number;
    WR: number;
    TE: number;
    K: number;
    DEF: number;
    FLEX: number;
    SUPERFLEX?: number;
  };
  isLoading: boolean;
}

const MultiRosterContext = createContext<MultiRosterContextType | undefined>(undefined);

export const useMultiRoster = () => {
  const context = useContext(MultiRosterContext);
  if (!context) {
    throw new Error('useMultiRoster must be used within a MultiRosterProvider');
  }
  return context;
};

interface MultiRosterProviderProps {
  children: React.ReactNode;
  playersPool: Player[];
}

export const MultiRosterProvider: React.FC<MultiRosterProviderProps> = ({ 
  children, 
  playersPool 
}) => {
  const { user } = useUser();
  const [rosters, setRosters] = useState<UserRoster[]>([]);
  const [activeRosterId, setActiveRosterId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);


  // Load rosters from Clerk metadata (syncs across devices)
  const loadRosters = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Load rosters from user metadata
      const stored = user.unsafeMetadata?.fantasyRosters as any[];
      if (stored && Array.isArray(stored)) {
        const parsedRosters = stored.map((r: any) => ({
          ...r,
          createdAt: new Date(r.createdAt),
          updatedAt: new Date(r.updatedAt)
        }));
        setRosters(parsedRosters);
        
        // Set active roster from metadata
        const activeId = user.unsafeMetadata?.activeRosterId as string;
        if (activeId && parsedRosters.find((r: UserRoster) => r.id === activeId)) {
          setActiveRosterId(activeId);
        } else if (parsedRosters.length > 0) {
          setActiveRosterId(parsedRosters[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load rosters:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Save rosters to Clerk metadata (syncs across devices)
  const saveRosters = useCallback(async (newRosters: UserRoster[]) => {
    if (!user) return;
    
    try {
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          fantasyRosters: newRosters.map(r => ({
            ...r,
            createdAt: r.createdAt.toISOString(),
            updatedAt: r.updatedAt.toISOString()
          }))
        }
      });
      setRosters(newRosters);
    } catch (error) {
      console.error('Failed to save rosters:', error);
    }
  }, [user]);

  // Save active roster ID to Clerk metadata
  const saveActiveRoster = useCallback(async (rosterId: string) => {
    if (!user) return;
    
    try {
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          activeRosterId: rosterId
        }
      });
      setActiveRosterId(rosterId);
    } catch (error) {
      console.error('Failed to save active roster:', error);
    }
  }, [user]);

  // Load rosters on mount and user change
  useEffect(() => {
    if (user !== undefined) { // Wait for user to be loaded
      loadRosters();
    }
  }, [user, loadRosters]);

  // Create a new roster
  const createRoster = useCallback(async (name: string, formatId: string): Promise<string> => {
    setIsLoading(true);
    try {
      const format = ROSTER_FORMATS[formatId];
      if (!format) {
        throw new Error('Invalid format');
      }

      const newRoster: UserRoster = {
        id: `roster-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        name,
        format,
        players: [],
        starters: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: rosters.length === 0 // First roster is active by default
      };

      const newRosters = [...rosters, newRoster];
      await saveRosters(newRosters);
      
      // Set as active if it's the first roster
      if (rosters.length === 0) {
        await saveActiveRoster(newRoster.id);
      }

      return newRoster.id;
    } finally {
      setIsLoading(false);
    }
  }, [rosters, saveRosters, saveActiveRoster]);

  // Delete a roster
  const deleteRoster = useCallback(async (rosterId: string): Promise<void> => {
    setIsLoading(true);
    try {
      const newRosters = rosters.filter(r => r.id !== rosterId);
      await saveRosters(newRosters);

      // If we deleted the active roster, set a new one
      if (activeRosterId === rosterId) {
        if (newRosters.length > 0) {
          await saveActiveRoster(newRosters[0].id);
        } else {
          setActiveRosterId(null);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [rosters, activeRosterId, saveRosters, saveActiveRoster]);

  // Set active roster
  const setActiveRoster = useCallback(async (rosterId: string) => {
    const roster = rosters.find(r => r.id === rosterId);
    if (roster) {
      await saveActiveRoster(rosterId);
    }
  }, [rosters, saveActiveRoster]);

  // Update roster name
  const updateRosterName = useCallback(async (rosterId: string, name: string): Promise<void> => {
    setIsLoading(true);
    try {
      const newRosters = rosters.map(r => 
        r.id === rosterId 
          ? { ...r, name, updatedAt: new Date() }
          : r
      );
      await saveRosters(newRosters);
    } finally {
      setIsLoading(false);
    }
  }, [rosters, saveRosters]);

  // Get effective starter limits for a roster (custom overrides or format defaults)
  const getStarterLimits = useCallback((rosterId: string) => {
    const roster = rosters.find(r => r.id === rosterId);
    if (!roster?.format?.starters) {
      return {
        QB: 1,
        RB: 2,
        WR: 2,
        TE: 1,
        K: 1,
        DEF: 1,
        FLEX: 1,
        SUPERFLEX: 0
      };
    }
    
    return {
      ...roster.format.starters,
      ...roster.customStarterLimits
    };
  }, [rosters]);

  // Add player to roster
  const addPlayerToRoster = useCallback(async (rosterId: string, player: Player): Promise<void> => {
    setIsLoading(true);
    try {
      const newRosters = rosters.map(r => {
        if (r.id === rosterId) {
          const updatedPlayers = [...r.players.filter(id => id !== String(player.id)), String(player.id)];
          const currentStarters = r.starters || [];
          
          // Auto-assign as starter if we have room based on effective limits
          const starterLimits = getStarterLimits(r.id);

          
          const currentStartersOfPosition = currentStarters.filter(starterId => {
            const foundPlayer = playersPool.find(p => String(p.id) === starterId);
            return foundPlayer?.position === player.position;
          });

          let newStarters = currentStarters;
          const starterLimit = starterLimits[player.position as keyof typeof starterLimits] || 0;
          
          // If we have fewer starters than allowed for this position, make this player a starter
          if (currentStartersOfPosition.length < starterLimit && starterLimit > 0) {
            newStarters = [...currentStarters.filter(id => id !== String(player.id)), String(player.id)];
          }
          
          return { 
            ...r, 
            players: updatedPlayers,
            starters: newStarters,
            updatedAt: new Date() 
          };
        }
        return r;
      });
      await saveRosters(newRosters);
    } finally {
      setIsLoading(false);
    }
  }, [rosters, playersPool, saveRosters, getStarterLimits]);

  // Remove player from roster
  const removePlayerFromRoster = useCallback(async (rosterId: string, playerId: string): Promise<void> => {
    setIsLoading(true);
    try {
      // Find the player name for the toast
      const player = playersPool.find(p => String(p.id) === playerId);
      const playerName = player?.name || 'Player';
      
      const newRosters = rosters.map(r => 
        r.id === rosterId 
          ? { 
              ...r, 
              players: r.players.filter(id => id !== playerId),
              starters: (r.starters || []).filter(id => id !== playerId),
              updatedAt: new Date() 
            }
          : r
      );
      await saveRosters(newRosters);
      
      // Show toast notification
      toast.success(`${playerName} removed from roster!`, {
        duration: 2500,
        position: 'top-center',
      });
    } finally {
      setIsLoading(false);
    }
  }, [rosters, playersPool, saveRosters]);

  // Replace all players in roster
  const replaceRosterPlayers = useCallback(async (rosterId: string, players: Player[]): Promise<void> => {
    setIsLoading(true);
    try {
      const newRosters = rosters.map(r => {
        if (r.id === rosterId) {
          const playerIds = players.map(p => String(p.id));
          
          // Auto-assign starters based on effective limits
          const starterLimits = getStarterLimits(r.id);
          const newStarters: string[] = [];
          
          // Group players by position
          const playersByPosition: Record<string, Player[]> = {};
          players.forEach(player => {
            if (!playersByPosition[player.position]) {
              playersByPosition[player.position] = [];
            }
            playersByPosition[player.position].push(player);
          });
          
          // Assign starters for each position up to the limit
          Object.keys(starterLimits).forEach(position => {
            const positionPlayers = playersByPosition[position] || [];
            const limit = starterLimits[position as keyof typeof starterLimits] || 0;
            
            // Take the first N players as starters (could be sorted by value in the future)
            const startersForPosition = positionPlayers
              .slice(0, limit)
              .map(p => String(p.id));
            
            newStarters.push(...startersForPosition);
          });
          
          return { 
            ...r, 
            players: playerIds,
            starters: newStarters,
            updatedAt: new Date() 
          };
        }
        return r;
      });
      await saveRosters(newRosters);
    } finally {
      setIsLoading(false);
    }
  }, [rosters, saveRosters, getStarterLimits]);

  // Replace all players in roster with specific starter designations
  const replaceRosterPlayersWithStarters = useCallback(async (rosterId: string, players: Player[], starterPlayerIds?: string[]): Promise<void> => {
    setIsLoading(true);
    try {
      const newRosters = rosters.map(r => {
        if (r.id === rosterId) {
          const playerIds = players.map(p => String(p.id));
          
          let newStarters: string[] = [];
          
          if (starterPlayerIds && starterPlayerIds.length > 0) {
            // Use provided starter IDs if available
            newStarters = starterPlayerIds.filter(id => playerIds.includes(id));
          } else {
            // Fallback to auto-assignment logic
            const starterLimits = getStarterLimits(r.id);
            
            // Group players by position
            const playersByPosition: Record<string, Player[]> = {};
            players.forEach(player => {
              if (!playersByPosition[player.position]) {
                playersByPosition[player.position] = [];
              }
              playersByPosition[player.position].push(player);
            });
            
            // Assign starters for each position up to the limit
            Object.keys(starterLimits).forEach(position => {
              const positionPlayers = playersByPosition[position] || [];
              const limit = starterLimits[position as keyof typeof starterLimits] || 0;
              
              // Take the first N players as starters (could be sorted by value in the future)
              const startersForPosition = positionPlayers
                .slice(0, limit)
                .map(p => String(p.id));
              
              newStarters.push(...startersForPosition);
            });
          }
          
          return { 
            ...r, 
            players: playerIds,
            starters: newStarters,
            updatedAt: new Date() 
          };
        }
        return r;
      });
      await saveRosters(newRosters);
    } finally {
      setIsLoading(false);
    }
  }, [rosters, saveRosters, getStarterLimits]);

  // Toggle player starter status
  const togglePlayerStarter = useCallback(async (rosterId: string, playerId: string, isStarter: boolean): Promise<void> => {
    setIsLoading(true);
    try {
      // Find the player name for the toast
      const player = playersPool.find(p => String(p.id) === playerId);
      const playerName = player?.name || 'Player';
      
      const newRosters = rosters.map(r => {
        if (r.id === rosterId) {
          const currentStarters = r.starters || [];
          const newStarters = isStarter
            ? [...currentStarters.filter(id => id !== playerId), playerId]
            : currentStarters.filter(id => id !== playerId);
          
          return { 
            ...r, 
            starters: newStarters,
            updatedAt: new Date() 
          };
        }
        return r;
      });
      await saveRosters(newRosters);
      
      // Show toast notification
      if (isStarter) {
        toast.success(`${playerName} moved to starting lineup!`, {
          duration: 2500,
          position: 'top-center',
        });
      } else {
        toast.success(`${playerName} moved to bench!`, {
          duration: 2500,
          position: 'top-center',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [rosters, playersPool, saveRosters]);

  // Update starter limits for a roster
  const updateStarterLimits = useCallback(async (rosterId: string, limits: Partial<UserRoster['customStarterLimits']>): Promise<void> => {
    setIsLoading(true);
    try {
      const newRosters = rosters.map(r => 
        r.id === rosterId 
          ? { 
              ...r, 
              customStarterLimits: { ...r.customStarterLimits, ...limits },
              updatedAt: new Date() 
            }
          : r
      );
      saveRosters(newRosters);
    } finally {
      setIsLoading(false);
    }
  }, [rosters, saveRosters]);

  // Check if player is in active roster
  const isPlayerInActiveRoster = useCallback((playerId: string): boolean => {
    const activeRoster = rosters.find(r => r.id === activeRosterId);
    return activeRoster ? activeRoster.players.includes(playerId) : false;
  }, [rosters, activeRosterId]);

  // Get active roster
  const activeRoster = rosters.find(r => r.id === activeRosterId) || null;

  // Get active roster players
  const activeRosterPlayers = useMemo(() => {
    if (!activeRoster) return [];
    return activeRoster.players
      .map(playerId => playersPool.find(p => String(p.id) === playerId))
      .filter((player): player is Player => player !== undefined);
  }, [activeRoster, playersPool]);

  const value: MultiRosterContextType = {
    rosters,
    activeRosterId,
    activeRoster,
    activeRosterPlayers,
    playersPool,
    createRoster,
    deleteRoster,
    setActiveRoster,
    updateRosterName,
    updateStarterLimits,
    addPlayerToRoster,
    removePlayerFromRoster,
    replaceRosterPlayers,
    replaceRosterPlayersWithStarters,
    togglePlayerStarter,
    isPlayerInActiveRoster,
    getStarterLimits,
    isLoading
  };

  return (
    <MultiRosterContext.Provider value={value}>
      {children}
    </MultiRosterContext.Provider>
  );
};